/**
 * AroundMeWidget — country-aware "Around me" strip.
 *
 * Shows 4 etiquette pills (e.g., Pharmacy / Café / ATM / Bakery in MA;
 * Mosque / Halal Restaurant / Halal Butcher / Pharmacy in FR) based on
 * `countryCode` from LocationService. Tap a pill → bottom sheet with the
 * 5 closest matching POIs from OpenStreetMap (Overpass API, free).
 *
 * Empty state: if no location, render a single "Set your city" prompt.
 *
 * Privacy: lat/lon stays on-device (Overpass query is the only network
 * call, no server middleman). User opt-in only — pills don't auto-fetch.
 */

import type { Widget, WidgetContext } from '../types';
import { aroundMeService, type PoiEtiquette, type PoiResult } from '../../../services/AroundMeService';

function localizedLabel(et: PoiEtiquette, locale: string): string {
  const lang = (locale || 'en').slice(0, 2) as keyof PoiEtiquette['labels'];
  return et.labels[lang] || et.labels.en;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDistance(m: number, locale: string): string {
  if (m < 950) return `${m} m`;
  const km = m / 1000;
  try {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(km)} km`;
  } catch { return `${km.toFixed(1)} km`; }
}

function injectStyles(): void {
  if (document.getElementById('aweni-aroundme-styles')) return;
  const style = document.createElement('style');
  style.id = 'aweni-aroundme-styles';
  style.textContent = `
    .aweni-aroundme {
      background: var(--card-bg);
      border: 1px solid var(--border);
      border-radius: 16px;
      padding: 14px 16px;
    }
    .aweni-aroundme-title {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: var(--text-secondary);
      margin-bottom: 10px;
    }
    .aweni-aroundme-pills {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
    }
    @media (max-width: 360px) {
      .aweni-aroundme-pills { grid-template-columns: repeat(2, 1fr); }
    }
    .aweni-aroundme-pill {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 10px 4px;
      border-radius: 12px;
      background: var(--bg-secondary);
      color: var(--text-primary);
      border: 1px solid transparent;
      cursor: pointer;
      transition: border-color 120ms ease, transform 120ms ease;
      min-height: 64px;
    }
    .aweni-aroundme-pill:hover { border-color: var(--accent); }
    .aweni-aroundme-pill:active { transform: scale(0.96); }
    .aweni-aroundme-pill-icon { font-size: 22px; line-height: 1; }
    .aweni-aroundme-pill-label {
      font-size: 11px;
      font-weight: 600;
      text-align: center;
      line-height: 1.2;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    /* Bottom sheet for results */
    .aweni-aroundme-sheet-overlay {
      position: fixed; inset: 0; z-index: 9050;
      background: rgba(15,23,42,0.6);
      display: flex; align-items: flex-end; justify-content: center;
    }
    @media (min-width: 768px) { .aweni-aroundme-sheet-overlay { align-items: center; } }
    .aweni-aroundme-sheet {
      background: var(--card-bg);
      color: var(--text-primary);
      width: 100%;
      max-width: 480px;
      border-radius: 24px 24px 0 0;
      padding: 20px 16px 24px;
      max-height: 70vh;
      overflow-y: auto;
    }
    @media (min-width: 768px) { .aweni-aroundme-sheet { border-radius: 24px; } }
    .aweni-aroundme-sheet-header {
      display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
    }
    .aweni-aroundme-sheet-icon { font-size: 24px; }
    .aweni-aroundme-sheet-title {
      font-size: 18px; font-weight: 700; flex: 1;
    }
    .aweni-aroundme-sheet-close {
      width: 36px; height: 36px; border-radius: 50%;
      background: var(--bg-secondary); border: none;
      font-size: 22px; color: var(--text-secondary); cursor: pointer;
    }
    .aweni-aroundme-result {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 14px; border-radius: 12px;
      background: var(--bg-secondary);
      margin-bottom: 8px;
      text-decoration: none;
      color: var(--text-primary);
    }
    .aweni-aroundme-result-name { font-size: 14px; font-weight: 600; }
    .aweni-aroundme-result-distance {
      font-size: 12px; color: var(--text-secondary); font-weight: 600;
    }
    .aweni-aroundme-loading, .aweni-aroundme-empty {
      text-align: center; padding: 24px 12px;
      color: var(--text-secondary); font-size: 14px;
    }
  `;
  document.head.appendChild(style);
}

function showResultsSheet(
  ctx: WidgetContext,
  etiquette: PoiEtiquette,
  results: PoiResult[] | null,
): void {
  const t = ctx.t;
  const locale = ctx.locale || 'en';
  const overlay = document.createElement('div');
  overlay.className = 'aweni-aroundme-sheet-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  const dismiss = () => overlay.remove();

  let body = '';
  if (results === null) {
    body = `<div class="aweni-aroundme-loading">${t('common_loading') || 'Loading…'}</div>`;
  } else if (results.length === 0) {
    body = `<div class="aweni-aroundme-empty">${t('aroundme_no_results') || 'No matching places nearby. Try widening your search.'}</div>`;
  } else {
    body = results.map((r) => `
      <a class="aweni-aroundme-result" href="${r.osmUrl || '#'}" target="_blank" rel="noopener noreferrer">
        <span class="aweni-aroundme-result-name">${escapeHtml(r.name)}</span>
        <span class="aweni-aroundme-result-distance">${formatDistance(r.distanceM, locale)}</span>
      </a>
    `).join('');
  }

  overlay.innerHTML = `
    <div class="aweni-aroundme-sheet" role="document">
      <div class="aweni-aroundme-sheet-header">
        <span class="aweni-aroundme-sheet-icon" aria-hidden="true">${etiquette.icon}</span>
        <span class="aweni-aroundme-sheet-title">${escapeHtml(localizedLabel(etiquette, locale))}</span>
        <button type="button" class="aweni-aroundme-sheet-close" aria-label="${t('common_close') || 'Close'}">×</button>
      </div>
      <div data-results>${body}</div>
    </div>
  `;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); });
  overlay.querySelector('.aweni-aroundme-sheet-close')?.addEventListener('click', dismiss);
  document.body.appendChild(overlay);
}

export const AroundMeWidget: Widget = {
  id: 'aroundme',
  label: { en: 'Around me', fr: 'Autour de moi', ar: 'حولي', es: 'Cerca de mí', de: 'In meiner Nähe' },
  icon: '📍',
  defaultVisible: true,
  defaultOrder: 9,
  defaultSize: 'compact',

  render(container: HTMLElement, _size, ctx: WidgetContext): void {
    injectStyles();
    const t = ctx.t;
    const locale = ctx.locale || 'en';
    const loc = ctx.getLocationService();

    // No location yet → silent hide so the home doesn't show an empty card.
    // The hero already has a "Choose your city" CTA; no need to repeat.
    if (loc.lat === null || loc.lng === null) {
      container.innerHTML = '';
      return;
    }

    const etiquettes = aroundMeService.getEtiquettesForCountry(loc.countryCode || null, 4);
    if (etiquettes.length === 0) { container.innerHTML = ''; return; }

    container.innerHTML = `
      <div class="aweni-aroundme" role="region" aria-label="${t('widget_aroundme') || 'Around me'}">
        <div class="aweni-aroundme-title">📍 ${t('widget_aroundme') || 'Around me'}</div>
        <div class="aweni-aroundme-pills">
          ${etiquettes.map((et) => `
            <button type="button" class="aweni-aroundme-pill" data-etiquette="${et.id}"
                    aria-label="${escapeHtml(localizedLabel(et, locale))}">
              <span class="aweni-aroundme-pill-icon" aria-hidden="true">${et.icon}</span>
              <span class="aweni-aroundme-pill-label">${escapeHtml(localizedLabel(et, locale))}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;

    // Wire taps → open bottom sheet with results.
    container.querySelectorAll('.aweni-aroundme-pill').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = (btn as HTMLElement).dataset.etiquette || '';
        const et = etiquettes.find((e) => e.id === id);
        if (!et) return;
        // Show sheet in loading state, then populate
        showResultsSheet(ctx, et, null);
        const sheet = document.querySelector('.aweni-aroundme-sheet-overlay') as HTMLElement | null;
        const results = await aroundMeService.findNearby(et, loc.lat!, loc.lng!).catch(() => [] as PoiResult[]);
        if (sheet) {
          // Replace the loading body with results without recreating the sheet
          const body = sheet.querySelector('[data-results]') as HTMLElement | null;
          if (body) {
            if (results.length === 0) {
              body.innerHTML = `<div class="aweni-aroundme-empty">${t('aroundme_no_results') || 'No matching places nearby.'}</div>`;
            } else {
              body.innerHTML = results.map((r) => `
                <a class="aweni-aroundme-result" href="${r.osmUrl || '#'}" target="_blank" rel="noopener noreferrer">
                  <span class="aweni-aroundme-result-name">${escapeHtml(r.name)}</span>
                  <span class="aweni-aroundme-result-distance">${formatDistance(r.distanceM, locale)}</span>
                </a>
              `).join('');
            }
          }
        }
      });
    });
  },
};
